import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { fetchStatus, unlockApi, lockApi } from "../lib/api";

interface AuthState {
  setupComplete: boolean | null;
  unlocked: boolean;
  expiresAt?: number;
  offline: boolean;
}

interface AuthContextValue extends AuthState {
  refresh: () => Promise<void>;
  unlock: (passcode: string) => Promise<{ ok: boolean; remaining?: number; cooldownEndsAt?: number }>;
  lock: () => Promise<void>;
}

const Ctx = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ setupComplete: null, unlocked: false, offline: false });

  const refresh = async () => {
    const s = await fetchStatus();
    setState(prev => ({
      setupComplete: s.setupComplete ?? prev.setupComplete,
      unlocked: s.offline ? prev.unlocked : s.unlocked,
      expiresAt: s.expiresAt,
      offline: !!s.offline,
    }));
  };

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    // Periodic check at 5-min cadence so a tab left open eventually re-locks too
    const id = setInterval(() => refresh(), 5 * 60_000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
    };
  }, []);

  const unlock = async (passcode: string) => {
    const result = await unlockApi(passcode);
    if (result.ok) await refresh();
    return result;
  };

  const lock = async () => {
    await lockApi();
    await refresh();
  };

  return <Ctx.Provider value={{ ...state, refresh, unlock, lock }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
